import React, { useState, useEffect } from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {Modal, IconButton, Alert} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import {newSupportTicket} from "../../../components/business_logic/support_ticket_requests";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import CircularProgress from "@mui/material/CircularProgress";
import {Build} from "../../../models/build";
import {fetchBuildsPaginated} from "../../../components/business_logic/build_requests";
import BuildSelector from "../templates/build_selector";

interface NewSupportTicketProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export default function NewSupportTicket({open, onClose, onSuccess}: NewSupportTicketProps) {
    const [buildList, setBuildList] = useState<Build[]>([]);
    const [selectedBuildId, setSelectedBuildId] = useState<string>('');
    const [loadingBuilds, setLoadingBuilds] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [buildError, setBuildError] = useState(false);
    const [buildErrorMessage, setBuildErrorMessage] = useState('');
    const [titleError, setTitleError] = useState(false);
    const [titleErrorMessage, setTitleErrorMessage] = useState('');
    const [descriptionError, setDescriptionError] = useState(false);
    const [descriptionErrorMessage, setDescriptionErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Load builds when modal opens
    useEffect(() => {
        if (open) {
            loadBuilds().then();
            resetForm();
        }
    }, [open]);

    const loadBuilds = async () => {
        setLoadingBuilds(true);
        try {
            const builds = await fetchBuildsPaginated(1);
            setBuildList(builds);
        } catch (error) {
            console.error('Failed to load builds:', error);
        } finally {
            setLoadingBuilds(false);
        }
    };

    const resetForm = () => {
        setSelectedBuildId('');
        setTitle('');
        setDescription('');
        setBuildError(false);
        setBuildErrorMessage('');
        setTitleError(false);
        setTitleErrorMessage('');
        setDescriptionError(false);
        setDescriptionErrorMessage('');
        setSubmitSuccess(false);
        setSubmitError('');
    };

    const handleClose = () => {
        if (!isSubmitting) {
            resetForm();
            onClose();
        }
    };

    const validateInputs = () => {
        let isValid = true;

        if (!selectedBuildId || selectedBuildId.trim().length === 0) {
            setBuildError(true);
            setBuildErrorMessage('Build selection is required.');
            isValid = false;
        } else {
            setBuildError(false);
            setBuildErrorMessage('');
        }

        if (!title || title.trim().length === 0) {
            setTitleError(true);
            setTitleErrorMessage('Title is required.');
            isValid = false;
        } else {
            setTitleError(false);
            setTitleErrorMessage('');
        }

        if (!description || description.trim().length === 0) {
            setDescriptionError(true);
            setDescriptionErrorMessage('Description is required.');
            isValid = false;
        } else if (description.trim().length < 10) {
            setDescriptionError(true);
            setDescriptionErrorMessage('Description must be at least 10 characters.');
            isValid = false;
        } else {
            setDescriptionError(false);
            setDescriptionErrorMessage('');
        }

        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateInputs()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitSuccess(false);
        setSubmitError('');

        try {
            const newTicket = {
                id: '',
                user: {} as any,
                title: title.trim(),
                description: description.trim(),
                ticket_status: 'open',
                build: selectedBuildId,
            };

            const result = await newSupportTicket(newTicket);
            
            if (result && Object.keys(result).length > 0) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    resetForm();
                    onClose();
                    if (onSuccess) {
                        onSuccess();
                    }
                }, 1500);
            } else {
                setSubmitError('Failed to create support ticket. Please try again.');
            }
        } catch (error) {
            setSubmitError('An error occurred while creating the support ticket.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal 
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && !isSubmitting) {
                    handleClose();
                }
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Box 
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    position: 'relative',
                    width: "90%",
                    maxWidth: "600px",
                    maxHeight: "90vh",
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    outline: 'none',
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 4,
                }}
            >
                <IconButton
                    onClick={handleClose}
                    disabled={isSubmitting}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        zIndex: 1,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                        '&:disabled': {
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    Create Support Ticket
                </Typography>

                {submitSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Support ticket created successfully!
                    </Alert>
                )}

                {submitError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {submitError}
                    </Alert>
                )}

                <Box sx={{ mb: 2 }}>
                    {loadingBuilds ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">
                                Loading builds...
                            </Typography>
                        </Box>
                    ) : (
                        <BuildSelector
                            builds={buildList}
                            value={selectedBuildId}
                            onChange={setSelectedBuildId}
                            label="Build"
                            required
                            error={buildError}
                            helperText={buildErrorMessage}
                            disabled={isSubmitting}
                        />
                    )}
                </Box>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel htmlFor="title">Title</FormLabel>
                    <TextField
                        error={titleError}
                        helperText={titleErrorMessage}
                        id="title"
                        type="text"
                        name="title"
                        placeholder="Brief description of your issue"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        fullWidth
                        variant="outlined"
                        color={titleError ? 'error' : 'primary'}
                        disabled={isSubmitting}
                        autoFocus
                    />
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                    <FormLabel htmlFor="description">Description</FormLabel>
                    <TextField
                        error={descriptionError}
                        helperText={descriptionErrorMessage}
                        id="description"
                        name="description"
                        placeholder="Please provide detailed information about your issue..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                        fullWidth
                        multiline
                        rows={6}
                        variant="outlined"
                        color={descriptionError ? 'error' : 'primary'}
                        disabled={isSubmitting}
                    />
                </FormControl>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />}
                    >
                        {isSubmitting ? 'Creating...' : 'Create Ticket'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
